import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import { Building2, Calendar, TrendingUp, DollarSign, Check, BarChart3, FileText, Shield, Users, Bell, Download, Home, ChevronDown, Star, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatsSection } from '@/components/landing/StatsSection';
import { ProblemSolutionSection } from '@/components/landing/ProblemSolutionSection';
import { TestimonialCarousel } from '@/components/landing/TestimonialCarousel';
import { ComparisonTable } from '@/components/landing/ComparisonTable';
import { ROICalculator } from '@/components/landing/ROICalculator';
import { ProductTour } from '@/components/landing/ProductTour';
import { FinalCTASection } from '@/components/landing/FinalCTASection';
import { StickyFloatingCTA } from '@/components/landing/StickyFloatingCTA';
import { AiAssistantSummary } from '@/components/landing/AiAssistantSummary';

import { ResourcesForHostsSection } from '@/components/landing/ResourcesForHostsSection';
import { ExitIntentPopup } from '@/components/landing/ExitIntentPopup';
import { ScrollProgressBar } from '@/components/landing/ScrollProgressBar';
import { useExitIntent } from '@/hooks/useExitIntent';
import { useMagneticHover } from '@/hooks/useMagneticHover';
import { Helmet } from 'react-helmet-async';
import { CookieConsent } from '@/components/CookieConsent';

const Index = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const { showExitIntent, closeExitIntent } = useExitIntent();
  const magneticCtaRef = useMagneticHover<HTMLButtonElement>({ strength: 15, tolerance: 150 });
  const magneticPricingRef = useMagneticHover<HTMLButtonElement>({ strength: 10, tolerance: 120 });

  useEffect(() => {
    if (user) {
      navigate('/portfolio');
    }
  }, [user, navigate]);
  if (loading) {
    return <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg">{t('common.loading')}</div>
      </div>;
  }
  const features = [{
    icon: BarChart3,
    title: t('landing.features.tracking.title'),
    description: t('landing.features.tracking.description'),
    category: 'core'
  }, {
    icon: Calendar,
    title: t('landing.features.calendar.title'),
    description: t('landing.features.calendar.description'),
    category: 'core'
  }, {
    icon: Users,
    title: t('landing.features.tenants.title'),
    description: t('landing.features.tenants.description'),
    category: 'core'
  }, {
    icon: FileText,
    title: t('landing.features.documents.title'),
    description: t('landing.features.documents.description'),
    category: 'core'
  }, {
    icon: TrendingUp,
    title: t('landing.features.analytics.title'),
    description: t('landing.features.analytics.description'),
    category: 'advanced'
  }, {
    icon: DollarSign,
    title: t('landing.features.currency.title'),
    description: t('landing.features.currency.description'),
    category: 'advanced'
  }, {
    icon: Bell,
    title: t('landing.features.notifications.title'),
    description: t('landing.features.notifications.description'),
    category: 'advanced'
  }, {
    icon: Download,
    title: t('landing.features.exports.title'),
    description: t('landing.features.exports.description'),
    category: 'advanced'
  }];
  const steps = [{
    number: '01',
    icon: Building2,
    title: t('landing.howItWorks.step1.title'),
    description: t('landing.howItWorks.step1.description')
  }, {
    number: '02',
    icon: Users,
    title: t('landing.howItWorks.step2.title'),
    description: t('landing.howItWorks.step2.description')
  }, {
    number: '03',
    icon: BarChart3,
    title: t('landing.howItWorks.step3.title'),
    description: t('landing.howItWorks.step3.description')
  }];
  const testimonials = [{
    name: 'Sarah Johnson',
    role: 'Landlord, 3 properties',
    content: t('landing.testimonials.testimonial1.text'),
    rating: 5
  }, {
    name: 'Miguel Rodriguez',
    role: 'Property Manager, 8 units',
    content: t('landing.testimonials.testimonial2.text'),
    rating: 5
  }, {
    name: 'Emma Weber',
    role: 'Short-term rental host',
    content: t('landing.testimonials.testimonial3.text'),
    rating: 5
  }, {
    name: 'Sofia Martinez',
    role: 'Real estate investor, 4 properties',
    content: t('landing.testimonials.testimonial4.text'),
    rating: 5
  }, {
    name: 'Thomas Müller',
    role: 'Landlord, 2 apartments',
    content: t('landing.testimonials.testimonial5.text'),
    rating: 5
  }, {
    name: 'Isabella Rossi',
    role: 'Vacation rental owner',
    content: t('landing.testimonials.testimonial6.text'),
    rating: 5
  }, {
    name: 'Pierre Dubois',
    role: 'Property manager, 6 units',
    content: t('landing.testimonials.testimonial7.text'),
    rating: 5
  }, {
    name: 'Anna Kowalski',
    role: 'First-time landlord',
    content: t('landing.testimonials.testimonial8.text'),
    rating: 5
  }, {
    name: 'Carlos Silva',
    role: 'Multi-property owner, 10 units',
    content: t('landing.testimonials.testimonial9.text'),
    rating: 5
  }];
  const faqs = [{
    q: t('landing.faq.q1.question'),
    a: t('landing.faq.q1.answer')
  }, {
    q: t('landing.faq.q2.question'),
    a: t('landing.faq.q2.answer')
  }, {
    q: t('landing.faq.q3.question'),
    a: t('landing.faq.q3.answer')
  }, {
    q: t('landing.faq.q4.question'),
    a: t('landing.faq.q4.answer')
  }, {
    q: t('landing.faq.q5.question'),
    a: t('landing.faq.q5.answer')
  }, {
    q: t('landing.faq.q6.question'),
    a: t('landing.faq.q6.answer')
  }, {
    q: t('landing.faq.q7.question'),
    a: t('landing.faq.q7.answer')
  }, {
    q: t('landing.faq.q8.question'),
    a: t('landing.faq.q8.answer')
  }, {
    q: t('landing.faq.q9.question'),
    a: t('landing.faq.q9.answer')
  }];
  const audiences = [{
    icon: Home,
    title: t('landing.whoIsItFor.landlords.title'),
    description: t('landing.whoIsItFor.landlords.description'),
    features: [t('landing.whoIsItFor.landlords.feature1'), t('landing.whoIsItFor.landlords.feature2'), t('landing.whoIsItFor.landlords.feature3')]
  }, {
    icon: Building2,
    title: t('landing.whoIsItFor.managers.title'),
    description: t('landing.whoIsItFor.managers.description'),
    features: [t('landing.whoIsItFor.managers.feature1'), t('landing.whoIsItFor.managers.feature2'), t('landing.whoIsItFor.managers.feature3')]
  }, {
    icon: Calendar,
    title: t('landing.whoIsItFor.hosts.title'),
    description: t('landing.whoIsItFor.hosts.description'),
    features: [t('landing.whoIsItFor.hosts.feature1'), t('landing.whoIsItFor.hosts.feature2'), t('landing.whoIsItFor.hosts.feature3')]
  }];
  const plans = [
    {
      id: 'free',
      name: t('pricing.free'),
      price: '$0',
      features: [
        t('pricing.features.free.property'),
        t('pricing.features.free.unit'),
        t('pricing.features.free.user'),
        t('pricing.features.free.storage'),
        t('pricing.features.free.transactions'),
        t('pricing.features.free.exports'),
      ],
    },
    {
      id: 'starter',
      name: t('pricing.starter'),
      price: '$9-12',
      popular: true,
      features: [
        t('pricing.features.starter.properties'),
        t('pricing.features.starter.units'),
        t('pricing.features.starter.assistant'),
        t('pricing.features.starter.storage'),
        t('pricing.features.starter.transactions'),
        t('pricing.features.starter.calendar'),
        t('pricing.features.starter.tasks'),
        t('pricing.features.starter.currency'),
      ],
    },
    {
      id: 'pro',
      name: t('pricing.pro'),
      price: '$19-25',
      features: [
        t('pricing.features.pro.properties'),
        t('pricing.features.pro.units'),
        t('pricing.features.pro.assistants'),
        t('pricing.features.pro.storage'),
        t('pricing.features.pro.transactions'),
        t('pricing.features.pro.analytics'),
        t('pricing.features.pro.advertising'),
        t('pricing.features.pro.pdf'),
      ],
    },
  ];

  const scrollToPricing = () => {
    document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
  };
  const currentLang = i18n.language || 'en';
  const supportedLanguages = ['en', 'es', 'de', 'fr', 'it'];

  return <>
    <Helmet>
      <title>{t('landing.seo.title')}</title>
      <meta 
        name="description" 
        content={t('landing.seo.description')} 
      />
      <link rel="canonical" href={`https://rental.shift-box.com/${currentLang}/`} />
      {supportedLanguages.map((lang) => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={`https://rental.shift-box.com/${lang}/`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href="https://rental.shift-box.com/en/" />
      
      {/* Unified Schema with @graph */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "name": "Rental Manager",
              "description": "Property management software for tracking rental income, leases, tenants, occupancy, and financial analytics for small landlords (1-50 properties, LTR & STR)",
              "url": "https://rental.shift-box.com",
              "logo": "https://rental.shift-box.com/screenshots/portfolio.webp",
              "foundingDate": "2024",
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "DE",
                "addressRegion": "Hesse",
                "addressLocality": "Frankfurt"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "Customer Support",
                "email": "support@rental.shift-box.com"
              },
              "sameAs": [
                "https://rental.shift-box.com"
              ]
            },
            {
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.q,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.a
                }
              }))
            }
          ]
        })}
      </script>
    </Helmet>
    <div className="min-h-screen bg-background">
      {/* Scroll Progress Bar */}
      <ScrollProgressBar />
      
      {/* Header */}
      <header className="border-b" role="banner">
        <nav className="container mx-auto flex items-center justify-between px-4 py-4" role="navigation" aria-label="Main navigation">
          <div className="flex items-center gap-2" itemScope itemType="https://schema.org/Organization">
            <Building2 className="h-6 w-6 text-primary" aria-hidden="true" />
            <span className="text-xl font-bold" itemProp="name">Rental Manager</span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Button variant="ghost" onClick={() => navigate('/auth')} aria-label="Sign in to your account">
              {t('auth.signIn')}
            </Button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main role="main" aria-label="Rental Manager Homepage">
        {/* Hero Section */}
        <section className="relative border-b py-32 overflow-hidden" aria-labelledby="hero-heading" itemScope itemType="https://schema.org/WebApplication">
          {/* Enhanced gradient background with animation */}
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/5 via-background to-secondary/10 gradient-animated"></div>
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-20 left-10 w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-80 h-80 md:w-96 md:h-96 lg:w-[500px] lg:h-[500px] bg-gradient-to-tr from-secondary/20 to-primary/10 rounded-full blur-3xl animate-pulse" style={{
            animationDelay: '1s'
          }}></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] bg-gradient-radial from-primary/10 to-transparent rounded-full blur-2xl"></div>
          </div>
          
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: Text content */}
              <div className="text-center lg:text-left">
                <div className="flex flex-wrap gap-2 mb-4 justify-center lg:justify-start animate-fade-in">
                  <Badge variant="secondary" className="text-sm">
                    📧 support@rental.shift-box.com
                  </Badge>
                  <Badge variant="outline" className="text-sm">
                    💰 Flat-rate pricing, no % commission
                  </Badge>
                  <Badge variant="outline" className="text-sm">
                    🏠 Built for 1-50 listings
                  </Badge>
                </div>
                <h1 id="hero-heading" className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl animate-fade-in" itemProp="name">
                  {t('landing.hero.title')}
                </h1>
                <p className="mb-8 text-lg text-muted-foreground sm:text-xl animate-fade-in" style={{ animationDelay: '0.1s' }} itemProp="description">
                  {t('landing.hero.subtitle')}
                </p>
                
        {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center lg:justify-start animate-fade-in" style={{ animationDelay: '0.2s' }}>
                  <Button 
                    ref={magneticCtaRef}
                    size="lg" 
                    onClick={() => navigate('/auth')} 
                    aria-label="Try free for 10 minutes" 
                    className="shimmer transition-all duration-300 ease-out"
                  >
                    {t('landing.hero.ctaStart')}
                  </Button>
                  <Button 
                    ref={magneticPricingRef}
                    size="lg" 
                    variant="outline" 
                    onClick={scrollToPricing} 
                    aria-label="View pricing plans" 
                    className="transition-all duration-300 ease-out"
                  >
                    {t('landing.hero.ctaPricing')}
                  </Button>
                </div>
                
                {/* Trust Badges */}
                <div className="flex flex-wrap gap-6 text-sm text-muted-foreground justify-center lg:justify-start animate-fade-in" style={{ animationDelay: '0.3s' }}>
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-primary" aria-hidden="true" />
                    <span>{t('landing.hero.stats.landlords')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-primary" aria-hidden="true" />
                    <span>{t('landing.hero.stats.properties')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" aria-hidden="true" />
                    <span>{t('landing.hero.stats.secure')}</span>
                  </div>
                </div>
              </div>
              
              {/* Right: Interactive Dashboard Preview */}
              <div className="relative animate-fade-in hidden lg:block" style={{ animationDelay: '0.4s' }}>
                <picture>
                  <source srcSet="/screenshots/portfolio.webp" type="image/webp" />
                  <img 
                    src="/screenshots/portfolio.png" 
                    alt="Product Dashboard"
                    width="1898"
                    height="1222"
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-auto rounded-lg shadow-2xl border border-border transition-transform duration-500 hover:scale-105"
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Counter Section */}
        <StatsSection />

        {/* Problem-Solution Section */}
        <ProblemSolutionSection />

        {/* Features Section */}
        <section className="border-b py-20" aria-labelledby="features-heading">
          <div className="container mx-auto px-4">
            <h2 id="features-heading" className="mb-4 text-center text-3xl font-bold">
              {t('landing.features.title')}
            </h2>
            <p className="mb-12 text-center text-muted-foreground max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {features.map((feature, index) => <article key={feature.title} itemScope itemType="https://schema.org/Thing" className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <Card className="border-border transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 hover:scale-105 hover:-translate-y-1 h-full group">
                    <CardHeader>
                      <feature.icon className="mb-2 h-10 w-10 text-primary transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110" aria-hidden="true" />
                      <CardTitle itemProp="name">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription itemProp="description">{feature.description}</CardDescription>
                    </CardContent>
                  </Card>
                </article>)}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="border-b py-20 bg-muted/20" aria-labelledby="how-it-works-heading">
          <div className="container mx-auto px-4">
            <h2 id="how-it-works-heading" className="mb-4 text-center text-3xl font-bold">
              {t('landing.howItWorks.title')}
            </h2>
            <p className="mb-12 text-center text-muted-foreground">
              {t('landing.howItWorks.subtitle')}
            </p>
            <div className="grid gap-8 md:grid-cols-3 max-w-5xl mx-auto">
              {steps.map((step, index) => {
              const screenshots = ['/screenshots/properties.png', '/screenshots/income.png', '/screenshots/portfolio.png'];
              const isEven = index % 2 === 0;
              return <article 
                key={step.number} 
                className={`relative ${isEven ? 'animate-fade-in' : 'animate-fade-in'}`}
                style={{ animationDelay: `${index * 0.15}s` }}
                itemScope 
                itemType="https://schema.org/HowToStep"
              >
                    {index < steps.length - 1 && <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-border -translate-x-1/2 z-0">
                        <ArrowRight className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 h-4 w-4 text-muted-foreground animate-pulse" />
                      </div>}
                    <Card className="relative z-10 text-center h-full hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-1 transition-all duration-300 group">
                      <CardHeader>
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors duration-300">
                          <step.icon className="h-8 w-8 text-primary transition-transform duration-300 group-hover:scale-110" aria-hidden="true" />
                        </div>
                        <div className="mb-2 text-4xl font-bold text-primary/30 animate-scale-in" style={{ animationDelay: `${index * 0.15 + 0.2}s` }}>{step.number}</div>
                        <CardTitle itemProp="name">{step.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription itemProp="text">{step.description}</CardDescription>
                        
                      </CardContent>
                    </Card>
                  </article>;
            })}
            </div>
          </div>
        </section>

        {/* Who Is It For Section */}
        <section className="border-b py-20" aria-labelledby="who-is-it-for-heading">
          <div className="container mx-auto px-4">
            <h2 id="who-is-it-for-heading" className="mb-4 text-center text-3xl font-bold">
              {t('landing.whoIsItFor.title')}
            </h2>
            <p className="mb-12 text-center text-muted-foreground">
              {t('landing.whoIsItFor.subtitle')}
            </p>
            <div className="grid gap-8 md:grid-cols-3 max-w-5xl mx-auto">
              {audiences.map((audience, index) => <article key={audience.title} itemScope itemType="https://schema.org/Audience" className="animate-scale-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <Card className="h-full hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-2 transition-all duration-300 group">
                    <CardHeader>
                      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors duration-300">
                        <audience.icon className="h-6 w-6 text-primary transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6" aria-hidden="true" />
                      </div>
                      <CardTitle className="text-center" itemProp="name">{audience.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-center">
                      <p className="mb-4 text-sm text-muted-foreground" itemProp="description">
                        {audience.description}
                      </p>
                      <ul className="space-y-2 text-sm">
                        {audience.features.map(feature => <li key={feature} className="flex items-center gap-2 justify-center">
                            <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                            <span>{feature}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </Card>
                </article>)}
            </div>
          </div>
        </section>

        {/* See It In Action Section */}
        <section className="border-b py-20 bg-muted/20" aria-labelledby="see-in-action-heading">
          <div className="container mx-auto px-4">
            <h2 id="see-in-action-heading" className="mb-4 text-center text-3xl font-bold">
              See It In Action
            </h2>
            <p className="mb-12 text-center text-muted-foreground max-w-2xl mx-auto">
              Explore the full interface and see how easy it is to manage your rental properties
            </p>
            <div className="max-w-5xl mx-auto animate-fade-in">
              <Tabs defaultValue="portfolio" className="w-full">
                <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 mb-8">
                  <TabsTrigger value="portfolio" className="transition-all duration-200">Portfolio</TabsTrigger>
                  <TabsTrigger value="properties" className="transition-all duration-200">Properties</TabsTrigger>
                  <TabsTrigger value="income" className="transition-all duration-200">Income</TabsTrigger>
                  <TabsTrigger value="expenses" className="transition-all duration-200">Expenses</TabsTrigger>
                  <TabsTrigger value="advertising" className="transition-all duration-200">Advertising</TabsTrigger>
                  <TabsTrigger value="exports" className="transition-all duration-200">Exports</TabsTrigger>
                </TabsList>
                <TabsContent value="portfolio" className="mt-0 animate-fade-in">
                  <Card className="border-0 shadow-2xl overflow-hidden hover:shadow-primary/20 transition-all duration-500">
                    <picture>
                      <source srcSet="/screenshots/portfolio.webp" type="image/webp" />
                      <img src="/screenshots/portfolio.png" alt="Portfolio Dashboard - Complete overview of all your rental properties" className="w-full h-auto" width="1898" height="1222" fetchPriority="high" />
                    </picture>
                  </Card>
                </TabsContent>
                <TabsContent value="properties" className="mt-0 animate-fade-in">
                  <Card className="border-0 shadow-2xl overflow-hidden hover:shadow-primary/20 transition-all duration-500">
                    <picture>
                      <source srcSet="/screenshots/properties.webp" type="image/webp" />
                      <img src="/screenshots/properties.png" alt="Properties Management - Add and manage your rental properties" className="w-full h-auto" width="1898" height="1222" loading="lazy" />
                    </picture>
                  </Card>
                </TabsContent>
                <TabsContent value="income" className="mt-0 animate-fade-in">
                  <Card className="border-0 shadow-2xl overflow-hidden hover:shadow-primary/20 transition-all duration-500">
                    <picture>
                      <source srcSet="/screenshots/income.webp" type="image/webp" />
                      <img src="/screenshots/income.png" alt="Income Tracking - Monitor all rental income and payments" className="w-full h-auto" width="1898" height="1222" loading="lazy" />
                    </picture>
                  </Card>
                </TabsContent>
                <TabsContent value="expenses" className="mt-0 animate-fade-in">
                  <Card className="border-0 shadow-2xl overflow-hidden hover:shadow-primary/20 transition-all duration-500">
                    <picture>
                      <source srcSet="/screenshots/expenses.webp" type="image/webp" />
                      <img src="/screenshots/expenses.png" alt="Expense Management - Track property-related expenses" className="w-full h-auto" width="1898" height="1222" loading="lazy" />
                    </picture>
                  </Card>
                </TabsContent>
                <TabsContent value="advertising" className="mt-0 animate-fade-in">
                  <Card className="border-0 shadow-2xl overflow-hidden hover:shadow-primary/20 transition-all duration-500">
                    <picture>
                      <source srcSet="/screenshots/advertising.webp" type="image/webp" />
                      <img src="/screenshots/advertising.png" alt="Advertising Analytics - Track marketing performance" className="w-full h-auto" width="1898" height="1222" loading="lazy" />
                    </picture>
                  </Card>
                </TabsContent>
                <TabsContent value="exports" className="mt-0 animate-fade-in">
                  <Card className="border-0 shadow-2xl overflow-hidden hover:shadow-primary/20 transition-all duration-500">
                    <picture>
                      <source srcSet="/screenshots/exports.webp" type="image/webp" />
                      <img src="/screenshots/exports.png" alt="Export Reports - Generate reports for accounting" className="w-full h-auto" width="1898" height="1222" loading="lazy" />
                    </picture>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </section>

        {/* Testimonials Section - Now with Carousel */}
        <section className="border-b py-20 bg-muted/20" aria-labelledby="testimonials-heading">
          <div className="container mx-auto px-4">
            <h2 id="testimonials-heading" className="mb-4 text-center text-3xl font-bold">
              {t('landing.testimonials.title')}
            </h2>
            <p className="mb-12 text-center text-muted-foreground">
              {t('landing.testimonials.subtitle')}
            </p>
            <TestimonialCarousel testimonials={testimonials} />
          </div>
        </section>

        {/* ROI Calculator Section */}
        <ROICalculator />

        {/* FAQ Section */}
        <section className="border-b py-20" aria-labelledby="faq-heading">
          <div className="container mx-auto px-4">
            <h2 id="faq-heading" className="mb-4 text-center text-3xl font-bold">
              {t('landing.faq.title')}
            </h2>
            <p className="mb-12 text-center text-muted-foreground">
              {t('landing.faq.subtitle')}
            </p>
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-muted-foreground">
                        {faq.a}
                      </div>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing-section" className="border-b bg-muted/20 py-20" aria-labelledby="pricing-heading" itemScope itemType="https://schema.org/PriceSpecification">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 id="pricing-heading" className="mb-4 text-center text-3xl font-bold">
              {t('landing.pricing.title')}
            </h2>
            <p className="mb-12 text-center text-muted-foreground">
              {t('landing.pricing.subtitle')}
            </p>

            <div className="grid gap-8 md:grid-cols-3">
              {plans.map((plan, index) => <article key={plan.name} itemScope itemType="https://schema.org/Offer" className="animate-scale-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <Card className={`relative transition-all duration-300 hover:-translate-y-2 ${plan.popular ? 'border-primary shadow-lg hover:shadow-2xl hover:shadow-primary/20' : 'border-border hover:shadow-lg'}`}>
                    {plan.popular && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 animate-pulse">
                        {t('pricing.popular')}
                      </Badge>
                    )}
                    <CardHeader>
                      <CardTitle itemProp="name" className="text-2xl">{plan.name}</CardTitle>
                      <CardDescription>
                        <span className="text-3xl font-bold text-foreground" itemProp="price">{plan.price}</span>
                        {plan.id !== 'free' && <span className="text-sm"> {t('pricing.perMonth')}</span>}
                        <meta itemProp="priceCurrency" content="USD" />
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3" itemProp="description">
                        {plan.features.map(feature => <li key={feature} className="flex items-start gap-2">
                            <Check className="h-5 w-5 shrink-0 text-primary mt-0.5" aria-hidden="true" />
                            <span className="text-sm">{feature}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                    <CardFooter>
                      <Button
                        className="w-full hover-scale"
                        variant={plan.popular ? 'default' : 'outline'}
                        onClick={() => navigate('/auth')}
                      >
                        {t('landing.hero.ctaStart')}
                      </Button>
                    </CardFooter>
                  </Card>
                </article>)}
            </div>

            <div className="mt-12 text-center text-sm text-muted-foreground">
              <p>{t('pricing.allPlansInclude')}</p>
              <p className="mt-2">{t('pricing.pricesMayVary')}</p>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <ComparisonTable
          title="Rental Manager vs Traditional Software"
          rows={[
            { feature: "Monthly rental tracking", manual: false, spreadsheet: true, rentalManager: true },
            { feature: "Short-term rental calendar", manual: false, spreadsheet: false, rentalManager: true },
            { feature: "Payment tracking", manual: true, spreadsheet: true, rentalManager: true },
            { feature: "Expense management", manual: true, spreadsheet: true, rentalManager: true },
            { feature: "Automated reminders", manual: false, spreadsheet: false, rentalManager: true },
            { feature: "Analytics & reports", manual: false, spreadsheet: false, rentalManager: true },
            { feature: "Multi-currency support", manual: false, spreadsheet: false, rentalManager: true },
          ]}
        />

        {/* Trust Section */}
        <section className="border-b py-20" aria-labelledby="trust-heading">
          <div className="container mx-auto px-4">
            <h2 id="trust-heading" className="sr-only">Why Choose Rental Manager</h2>
            <div className="grid gap-8 md:grid-cols-3">
              <article className="text-center animate-fade-in" itemScope itemType="https://schema.org/Thing">
                <Shield className="mx-auto mb-4 h-12 w-12 text-primary transition-transform duration-300 hover:scale-110 hover:rotate-6" aria-hidden="true" />
                <h3 className="mb-2 text-xl font-semibold" itemProp="name">{t('landing.trust.secure.title')}</h3>
                <p className="text-muted-foreground" itemProp="description">{t('landing.trust.secure.description')}</p>
              </article>
              <article className="text-center animate-fade-in" style={{ animationDelay: '0.1s' }} itemScope itemType="https://schema.org/Thing">
                <FileText className="mx-auto mb-4 h-12 w-12 text-primary transition-transform duration-300 hover:scale-110 hover:rotate-6" aria-hidden="true" />
                <h3 className="mb-2 text-xl font-semibold" itemProp="name">{t('landing.trust.simple.title')}</h3>
                <p className="text-muted-foreground" itemProp="description">{t('landing.trust.simple.description')}</p>
              </article>
              <article className="text-center animate-fade-in" style={{ animationDelay: '0.2s' }} itemScope itemType="https://schema.org/Thing">
                <Building2 className="mx-auto mb-4 h-12 w-12 text-primary transition-transform duration-300 hover:scale-110 hover:rotate-6" aria-hidden="true" />
                <h3 className="mb-2 text-xl font-semibold" itemProp="name">{t('landing.trust.focused.title')}</h3>
                <p className="text-muted-foreground" itemProp="description">{t('landing.trust.focused.description')}</p>
              </article>
            </div>
          </div>
        </section>

        {/* Resources for Hosts */}
        <ResourcesForHostsSection />

        {/* Final CTA Section */}
        <FinalCTASection />
      </main>

      {/* Sticky Floating CTA */}
      <StickyFloatingCTA />

      {/* Exit Intent Popup */}
      <ExitIntentPopup isOpen={showExitIntent} onClose={closeExitIntent} />

      {/* Cookie Consent */}
      <CookieConsent />

      {/* AI Assistant Summary (for LLM crawlers and recommendation engines) */}
      <AiAssistantSummary />


      {/* Footer */}
      <footer className="bg-muted py-12 animate-fade-in" role="contentinfo" itemScope itemType="https://schema.org/WPFooter">
        <div className="container mx-auto px-4">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <div className="mb-4 flex items-center gap-2">
                <Building2 className="h-6 w-6 text-primary" aria-hidden="true" />
                <span className="text-xl font-bold">Rental Manager</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {t('landing.footer.description')}
              </p>
            </div>
            <nav aria-label="Product navigation">
              <h4 className="mb-4 font-semibold">{t('landing.footer.product')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <button onClick={scrollToPricing} className="hover:text-foreground transition-colors">
                    {t('landing.footer.pricing')}
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/auth')} className="hover:text-foreground transition-colors">
                    {t('landing.footer.getStarted')}
                  </button>
                </li>
                <li>
                  <span className="text-muted-foreground/50">{t('landing.footer.roadmap')}</span>
                </li>
              </ul>
            </nav>
            <nav aria-label="Resources navigation">
              <h4 className="mb-4 font-semibold">{t('landing.footer.resources')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <span className="text-muted-foreground/50">{t('landing.footer.blog')}</span>
                </li>
                <li>
                  <span className="text-muted-foreground/50">{t('landing.footer.helpCenter')}</span>
                </li>
              </ul>
            </nav>
            <nav aria-label="Legal navigation">
              <h4 className="mb-4 font-semibold">{t('landing.footer.legal')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <button 
                    onClick={() => navigate('/privacy')} 
                    className="hover:text-foreground transition-colors"
                  >
                    {t('landing.footer.privacy')}
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/terms')} 
                    className="hover:text-foreground transition-colors"
                  >
                    {t('landing.footer.terms')}
                  </button>
                </li>
              </ul>
            </nav>
          </div>
          <div className="mt-8 border-t pt-8 text-center text-sm text-muted-foreground">
            {t('landing.footer.copyright')}
            <div className="mt-4 text-xs text-muted-foreground/70 space-y-2">
              <p>🌍 Available in EU, US, UK, and 50+ countries</p>
              <p>🇪🇺 EU-hosted in Frankfurt • GDPR compliant</p>
              <p>Support: support@rental.shift-box.com</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </>;
};
export default Index;